/**
 * THE PHOTO FAN — the shelf's cards laid out as a hand (D-032).
 *
 * The design authority, 2026-09-01, handed over a card-fan carousel — seven
 * slot poses, an elastic entry, arrows and dots — and asked for it *"for the
 * photo component at the top of each survey screen."* This module is the
 * ARRANGEMENT and nothing else, the way `photoShelf.ts` is: which card sits in
 * which slot, where the front goes after a photo is added or deleted, and how
 * far the hand may spread inside the stage. It decides nothing about which
 * cards exist (that is `shelfModel`) and animates nothing (that is GSAP, in
 * `PhotoFan.tsx`, applying only what these functions return).
 *
 * ── The seven poses, as handed over ─────────────────────────────────────────
 * `FAN_POSES` is the carousel's `FAN_POSITIONS` table verbatim — rotation in
 * degrees, scale, x and y in rem (16px), z-order. The middle slot is upright,
 * full size and on top; every other slot is the mirror of its opposite. The
 * hand-over's `getSlotConfig` interpolated these for fewer than seven cards;
 * here fewer cards simply leave slots empty, so a hand of three looks like a
 * hand of seven with four cards missing rather than a different, wider fan.
 *
 * ── No wrap ─────────────────────────────────────────────────────────────────
 * The hand-over paged modulo the card count when there were more than seven.
 * A shot list is ORDERED — shots in catalog order, then extras in capture
 * order — and looping from the last extra to the first recommended shot would
 * say "next" about a card that is first. So the window slides and stops: at
 * the start of the hand the fan opens to the right, at the end to the left,
 * and the arrows disable where there is nothing further.
 *
 * ── The spread is the stage's, not the viewport's ───────────────────────────
 * The hand-over scaled x by `window.innerWidth` breakpoints tuned for a hero
 * section. The shelf is full-bleed at 430px with 168px cards, so the spread is
 * SOLVED from the stage width and the outermost card's rotated extent: the
 * hand is as wide as it can be without any card's corner leaving the screen.
 *
 * Pure: no React, no DOM. `tests/unit/photo-fan.test.ts` runs it directly.
 */

/** Slots in the window. */
export const FAN_SLOTS = 7;
/** The middle slot — where the fronted card sits. */
export const FAN_HALF = 3;

export interface FanPose {
  /** Degrees, clockwise positive. */
  rot: number;
  scale: number;
  /** In rem — multiply by the spread and 16 for px. */
  x: number;
  /** In rem — multiply by the drop factor and 16 for px. */
  y: number;
  /** Stacking order; the middle slot is on top. */
  z: number;
}

/** The hand-over's `FAN_POSITIONS`, unchanged. */
export const FAN_POSES: readonly FanPose[] = [
  { rot: -21, scale: 0.7756, x: -30, y: 7.3, z: 1 },
  { rot: -14, scale: 0.8498, x: -22, y: 4.0, z: 2 },
  { rot: -7, scale: 0.9346, x: -11, y: 1.3, z: 3 },
  { rot: 0, scale: 1.0, x: 0, y: 0.0, z: 10 },
  { rot: 7, scale: 0.9346, x: 11, y: 1.3, z: 3 },
  { rot: 14, scale: 0.8498, x: 22, y: 4.0, z: 2 },
  { rot: 21, scale: 0.7756, x: 30, y: 7.3, z: 1 },
];

const REM = 16;

/** The fronted card's index, held inside the hand; 0 for an empty hand. */
export function clampCenter(count: number, center: number): number {
  if (count <= 0) return 0;
  return Math.min(Math.max(center, 0), count - 1);
}

/** Where a section opens: the middle card, the balanced view. */
export function initialCenter(count: number): number {
  return clampCenter(count, count >> 1);
}

/**
 * card index → slot, for the seven-slot window centred on `center`. Cards
 * more than three away are absent — hidden, not wrapped.
 */
export function fanWindow(count: number, center: number): Map<number, number> {
  const map = new Map<number, number>();
  if (count <= 0) return map;
  const front = clampCenter(count, center);
  for (let slot = 0; slot < FAN_SLOTS; slot++) {
    const index = front + slot - FAN_HALF;
    if (index >= 0 && index < count) map.set(index, slot);
  }
  return map;
}

/**
 * Where the front goes once the cards have changed — keyed, because indices
 * shift when a card leaves.
 *
 *   - a card that is NEW comes to the front (the last of several — the last
 *     of a multi-pick is the one the surveyor just saw taken);
 *   - a card the caller names as `changed` comes to the front — a shot's card
 *     keeps its key when its photo lands, so the shelf says which one;
 *   - otherwise the previously fronted card stays fronted wherever it moved
 *     to, and if it is gone the front is handed to whatever now occupies its
 *     index, clamped to the hand.
 */
export function nextCenter(
  prevKeys: readonly string[],
  nextKeys: readonly string[],
  prevCenter: number,
  changed?: string,
): number {
  if (nextKeys.length === 0) return 0;
  if (changed !== undefined) {
    const at = nextKeys.indexOf(changed);
    if (at >= 0) return at;
  }
  const seen = new Set(prevKeys);
  let fresh = -1;
  nextKeys.forEach((key, index) => {
    if (!seen.has(key)) fresh = index;
  });
  if (fresh >= 0) return fresh;
  const prevKey = prevKeys[prevCenter];
  if (prevKey !== undefined) {
    const at = nextKeys.indexOf(prevKey);
    if (at >= 0) return at;
  }
  return clampCenter(nextKeys.length, prevCenter);
}

/**
 * The front, named directly by a tap — round 8.1 task 5. `nextCenter` above
 * answers "where did the front move to after the CARDS changed"; this
 * answers a different question a tap on a peeking card asks instead: "front
 * THIS one." Defensive rather than trusting the caller: a key the hand does
 * not carry — the tap and that very card's own deletion landing in the same
 * tick — leaves the front exactly where it was rather than pointing at
 * nothing.
 */
export function frontOn(
  keys: readonly string[],
  key: string,
  currentFront: string | undefined,
): string | undefined {
  return keys.includes(key) ? key : currentFront;
}

/**
 * The horizontal spread, 0..1, that fits the hand inside a stage: the
 * outermost slot's card — scaled, rotated, its axis-aligned box — ends at
 * the stage's edge. Floored so a very narrow stage still fans.
 */
export function fanSpread(stageWidth: number, cardWidth: number, cardHeight: number): number {
  const outer = FAN_POSES[FAN_SLOTS - 1] as FanPose;
  const rad = (Math.abs(outer.rot) * Math.PI) / 180;
  const extent = (outer.scale * (cardWidth * Math.cos(rad) + cardHeight * Math.sin(rad))) / 2;
  // A few px in hand: a rotated box's edge rounds, and the page must never
  // scroll sideways by a pixel (tests/e2e/layout-widths.spec.ts).
  const spread = (stageWidth / 2 - extent - EDGE_PX) / (outer.x * REM);
  return Math.min(1, Math.max(0.12, spread));
}

/** The air kept between the outermost card's corner and the stage's edge. */
export const EDGE_PX = 4;

export interface PosePx {
  x: number;
  y: number;
  rot: number;
  scale: number;
  z: number;
}

/** A slot's pose in px: x by the spread, y by the drop, rem → px. */
export function slotPosePx(slot: number, spread: number, drop: number): PosePx {
  const pose = FAN_POSES[Math.min(Math.max(slot, 0), FAN_SLOTS - 1)] as FanPose;
  return {
    x: pose.x * spread * REM,
    y: pose.y * drop * REM,
    rot: pose.rot,
    scale: pose.scale,
    z: pose.z,
  };
}

export type FanSide = 'left' | 'center' | 'right';

/**
 * Which side of the front a slot is on — the neighbour towards the middle
 * covers a side card's inner edge, so its outer corner is the one still
 * showing, and the card's ✓ moves there.
 */
export function fanSide(slot: number): FanSide {
  if (slot < FAN_HALF) return 'left';
  if (slot > FAN_HALF) return 'right';
  return 'center';
}
